import React, { useMemo } from "react";
import type { SalesTransaction } from "../../types";
import {
  BUCKETS,
  BUCKET_ORDER,
  NON_HERO_BUCKETS,
  classifyOutcome,
  type OutcomeBucketId,
} from "../../lib/outcomeBuckets";
import type { FilterableColumn, FilterMode } from "../../hooks/useTransactionFilters";

// =============================================================================
// OUTCOME SUMMARY STRIP — Dynamic
// =============================================================================
// Renders one cell per outcome bucket present in `transactions` (count > 0),
// in BUCKET_ORDER priority. Denominator = transactions.length, so percentages
// represent share-of-all (sums to <=100%, with the residue being unclassifiable
// rows in 'other').
//
// Used by:
// - DailyOutcomesChart (variant='full', big numbers — sits right of bar chart)
// - TransactionLog snippet mode (variant='compact', inline pills above table)
// =============================================================================

interface OutcomeSummaryStripProps {
  transactions: SalesTransaction[];
  variant?: "full" | "compact";
  // When provided, each bucket number becomes a filter button:
  // left-click isolates that bucket, right-click excludes it. Targets the
  // derived `bucket` filter column (see useTransactionFilters.getColumnValue).
  // Omit it (e.g. dashboard snippet with no filter machine) → static display.
  onApplyFilter?: (column: FilterableColumn, value: string, mode: FilterMode) => void;
}

const OutcomeSummaryStrip: React.FC<OutcomeSummaryStripProps> = ({
  transactions,
  variant = "full",
  onApplyFilter,
}) => {
  const interactive = !!onApplyFilter;

  const handleIsolate = (id: OutcomeBucketId) => {
    onApplyFilter?.("bucket", id, "isolate");
  };
  const handleExclude = (e: React.MouseEvent, id: OutcomeBucketId) => {
    if (!onApplyFilter) return;
    e.preventDefault(); // suppress native context menu only on filter cells
    onApplyFilter("bucket", id, "exclude");
  };
  const filterTitle = interactive ? "Click to isolate · Right-click to exclude" : undefined;
  const { counts, total } = useMemo(() => {
    const counts: Record<OutcomeBucketId, number> = {
      closed: 0,
      installment: 0,
      lost: 0,
      noshow: 0,
      rescheduled: 0,
      abandoned: 0,
      restored: 0,
      pending: 0,
      unique: 0,
      other: 0,
    };
    for (const t of transactions) {
      const id = classifyOutcome(t.callOutcome || "");
      counts[id]++;
    }
    return { counts, total: transactions.length };
  }, [transactions]);

  if (total === 0) {
    if (variant === "compact") return null;
    return (
      <div className="flex items-center justify-center liquid-glass-light p-6 min-h-[180px]">
        <p className="text-glass-muted text-sm">No outcome data</p>
      </div>
    );
  }

  const visibleBuckets = BUCKET_ORDER.filter((id) => counts[id] > 0);

  // ── Compact variant: inline pills (replaces snippet mode pills in TransactionLog) ──
  if (variant === "compact") {
    return (
      <div className="flex flex-wrap gap-1.5 mb-3">
        {visibleBuckets.map((id) => {
          const bucket = BUCKETS[id];
          const count = counts[id];
          return (
            <span
              key={id}
              data-bucket={interactive ? id : undefined}
              onClick={interactive ? () => handleIsolate(id) : undefined}
              onContextMenu={interactive ? (e) => handleExclude(e, id) : undefined}
              title={filterTitle}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full token-10 font-medium ${bucket.badgeClass} ${
                interactive
                  ? "cursor-pointer select-none transition-opacity hover:opacity-80 anim-tap"
                  : ""
              }`}
            >
              {bucket.label.charAt(0) + bucket.label.slice(1).toLowerCase()}{" "}
              <span className="font-bold">{count}</span>
            </span>
          );
        })}
      </div>
    );
  }

  // ── Full variant: big HERO cards + a separated zone for non-hero outcomes ──
  // Mako Jun 20: Abandoned / Pending Close / Unique are not performance heroes —
  // demote them below the line into their own quieter band (still filterable).
  const heroBuckets = visibleBuckets.filter((id) => !NON_HERO_BUCKETS.has(id));
  const secondaryBuckets = visibleBuckets.filter((id) => NON_HERO_BUCKETS.has(id));

  return (
    <div className="liquid-glass-light anim-lift px-4 py-5">
      {/* HERO cards — the performance outcomes that get the spotlight */}
      <div className="flex flex-wrap gap-y-3 gap-x-6 justify-start">
        {/* justify-start so 1-2 cells cluster left instead of spreading edge-to-edge. */}
        {heroBuckets.map((id) => {
          const bucket = BUCKETS[id];
          const count = counts[id];
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div
              key={id}
              data-bucket={interactive ? id : undefined}
              onClick={interactive ? () => handleIsolate(id) : undefined}
              onContextMenu={interactive ? (e) => handleExclude(e, id) : undefined}
              title={filterTitle}
              className={`text-center min-w-[64px] ${
                interactive
                  ? "cursor-pointer select-none -mx-2 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-black/[0.04] anim-tap"
                  : ""
              }`}
            >
              <div
                className="font-mono token-38 font-medium tabular-nums leading-none tracking-tight"
                style={{ color: bucket.textColorVar }}
              >
                {count}
              </div>
              <div className="token-10 font-semibold tracking-[0.10em] uppercase text-glass-muted mt-1.5 whitespace-nowrap">
                {bucket.label}
              </div>
              <div className="font-mono text-xs text-glass-secondary tabular-nums mt-0.5">
                {pct}%
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-4 pt-3 border-t border-[var(--glass-border-subtle)]">
        <span className="text-xs text-glass-muted">
          <span className="font-mono font-medium text-glass-secondary tabular-nums">{total}</span>{" "}
          total transaction{total !== 1 ? "s" : ""}
        </span>
      </div>

      {/* NON-HERO zone — edge/admin outcomes (Mako Jun 20: "not hero cards").
          Same card STRUCTURE as the heroes (number / UPPERCASE label / %) but a
          smaller token (token-24 vs token-38) + muted, so they read as the same
          family, just demoted. Still clickable to filter. */}
      {secondaryBuckets.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--glass-border-subtle)]">
          <div className="flex flex-wrap gap-y-3 gap-x-6 justify-center">
            {secondaryBuckets.map((id) => {
              const bucket = BUCKETS[id];
              const count = counts[id];
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div
                  key={id}
                  data-bucket={interactive ? id : undefined}
                  onClick={interactive ? () => handleIsolate(id) : undefined}
                  onContextMenu={interactive ? (e) => handleExclude(e, id) : undefined}
                  title={filterTitle}
                  className={`text-center min-w-[64px] opacity-80 ${
                    interactive
                      ? "cursor-pointer select-none -mx-2 -my-1 rounded-lg px-2 py-1 transition-all hover:opacity-100 hover:bg-black/[0.04] anim-tap"
                      : ""
                  }`}
                >
                  <div
                    className="font-mono token-24 tabular-nums leading-none tracking-tight"
                    style={{ color: bucket.textColorVar }}
                  >
                    {count}
                  </div>
                  <div className="token-10 font-semibold tracking-[0.10em] uppercase text-glass-muted mt-1.5 whitespace-nowrap">
                    {bucket.label}
                  </div>
                  <div className="font-mono text-xs text-glass-secondary tabular-nums mt-0.5">
                    {pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OutcomeSummaryStrip;
